/**
 * AI telemetry service.
 * Posts user feedback (thumbs up/down + optional comment) on AI-generated Jigs
 * to the JigAPI ai-telemetry proxy. Fire-and-forget by design: failures must
 * not surface to the user or block any UX path.
 */
import { AppConst } from '@/constants'
import axios from 'axios'
import { buildJigApiHeaders } from '@/services/generate-jig/generate-jig.auth'

declare var consoleError: any

const TELEMETRY_PROJECT_LOGS_PATH = '/ai-telemetry/v1/project_logs'
const SPARK_MODAL_FEEDBACK_SOURCE = 'spark_modal'

export type SparkModalFeedbackRating = 0 | 1

export interface SparkModalFeedbackInput {
  traceId: string
  rating: SparkModalFeedbackRating
  comment?: string
  jigHashId: string
  tenantId: number
  userId: string
}

export function getAiTelemetryProjectId(): string {
  const id = process.env.VUE_APP_AI_TELEMETRY_PROJECT_ID || ''
  return id.trim()
}

export function buildSparkFeedbackUrl(apiDomain: string, projectId: string): string {
  return apiDomain + TELEMETRY_PROJECT_LOGS_PATH + '/' + encodeURIComponent(projectId) + '/feedback'
}

interface SparkFeedbackEntry {
  id: string
  scores: { human_rating: SparkModalFeedbackRating }
  metadata: {
    feedback_source: typeof SPARK_MODAL_FEEDBACK_SOURCE
    user_id: string
    tenant_id: string
    jig_hash_id: string
  }
  comment?: string
}

export function buildSparkFeedbackBody(input: SparkModalFeedbackInput): { feedback: SparkFeedbackEntry[] } {
  const entry: SparkFeedbackEntry = {
    id: input.traceId,
    scores: { human_rating: input.rating },
    metadata: {
      feedback_source: SPARK_MODAL_FEEDBACK_SOURCE,
      user_id: input.userId || '',
      tenant_id: input.tenantId > 0 ? String(input.tenantId) : '',
      jig_hash_id: input.jigHashId || '',
    },
  }

  const trimmedComment = (input.comment || '').trim()
  if (trimmedComment.length > 0) {
    entry.comment = trimmedComment
  }

  return { feedback: [entry] }
}

/**
 * Sends a single spark-modal feedback entry to the JigAPI telemetry proxy.
 * Returns a Promise that always resolves — callers never need to await or
 * catch. Skips the request entirely when the projectId env var or trace_id is
 * missing, since the backend would reject those inputs anyway.
 */
export async function postSparkModalFeedback(input: SparkModalFeedbackInput): Promise<void> {
  const projectId = getAiTelemetryProjectId()
  if (!projectId || !input.traceId || input.tenantId <= 0) {
    return
  }

  const apiDomain = (AppConst.apiDomain || '').replace(/\/$/, '')
  const url = buildSparkFeedbackUrl(apiDomain, projectId)
  const body = buildSparkFeedbackBody(input)

  try {
    const headers = buildJigApiHeaders(input.tenantId, false)
    await axios.post(url, body, { headers })
  } catch (error) {
    if (typeof consoleError === 'function') {
      consoleError('Failed to post Spark modal feedback', error)
    }
  }
}
